/**
 * The territory "hex field": the GPU-side mirror of per-hex knowledge plus the shared
 * warped-hex lookup used by both the territory shader and CPU picking (they must agree,
 * or clicks near a border would pick the neighbour).
 */
import type { LoadedWorld } from '@sa/mapdata';

/** Per-hex flag bits in the state texture's alpha channel. */
export const HEX_LIVE = 1;
export const HEX_SEEN = 2;
export const HEX_FOCUS = 4;
/** Bits 3–7: time since the hex was last seen, in `STALE_STEP_TICKS` steps (0 = live, 31 = long ago / never). */
export const HEX_STALE_SHIFT = 3;
export const HEX_STALE_MAX = 31;
/** 2 s per staleness step: stale land fades over ~60 s (see the territory shader). */
export const STALE_STEP_TICKS = 20;

/** State texture width (texels); height = ceil(N / STATE_W). */
export const STATE_W = 128;

const SQRT3 = Math.sqrt(3);

/** Warp amplitude as a fraction of the hex radius: turns hex edges into organic borders. */
export const WARP_AMP = 0.7;
/**
 * Corner rounding between owners (smooth-min radius, in hex radii): a pixel in a hex corner
 * that two edges towards another owner round off belongs to that owner, so inland borders
 * read as curves instead of a hex sawtooth. Shared by the territory shader and picking.
 */
export const BORDER_ROUND = 0.8;

const EDGE_N: readonly [number, number][] = [
  [1, 0],
  [0.5, -0.8660254],
  [-0.5, -0.8660254],
  [-1, 0],
  [-0.5, 0.8660254],
  [0.5, 0.8660254],
];
const EDGE_D: readonly [number, number][] = [
  [1, 0],
  [1, -1],
  [0, -1],
  [-1, 0],
  [-1, 1],
  [0, 1],
];

function smin(a: number, b: number, k: number): number {
  const h = Math.min(1, Math.max(0, 0.5 + (0.5 * (b - a)) / k));
  return b + (a - b) * h - k * h * (1 - h);
}

// --- value noise for the warp field (baked once, see WarpField) ---
function fract(x: number): number {
  return x - Math.floor(x);
}
function hash12(px: number, py: number): number {
  let x = fract(px * 0.1031);
  let y = fract(py * 0.1031);
  let z = fract(px * 0.1031);
  const d = x * (y + 33.33) + y * (z + 33.33) + z * (x + 33.33);
  x += d;
  y += d;
  z += d;
  return fract((x + y) * z);
}
function vnoise(px: number, py: number): number {
  const ix = Math.floor(px);
  const iy = Math.floor(py);
  const fx = px - ix;
  const fy = py - iy;
  const ux = fx * fx * (3 - 2 * fx);
  const uy = fy * fy * (3 - 2 * fy);
  const a = hash12(ix, iy);
  const b = hash12(ix + 1, iy);
  const c = hash12(ix, iy + 1);
  const d = hash12(ix + 1, iy + 1);
  return a + (b - a) * ux + (c - a) * uy + (a - b - c + d) * ux * uy;
}

function warpRaw(x: number, y: number): [number, number] {
  const wx = (vnoise(x * 0.9, y * 0.9) + 0.5 * vnoise(x * 2.1 + 7.3, y * 2.1 + 7.3)) / 1.5 - 0.5;
  const wy = (vnoise(x * 0.9 + 19.1, y * 0.9 + 19.1) + 0.5 * vnoise(x * 2.1 + 3.7, y * 2.1 + 3.7)) / 1.5 - 0.5;
  return [wx, wy];
}

/** Warp field texels per mu. */
export const WARP_RES = 4;

/**
 * Domain warp applied before the hex lookup, baked once into an RG8 field (one texture
 * fetch in the shader). CPU picking samples the very same bytes with the same bilinear
 * filter, so clicks and pixels always agree on which hex is where.
 */
export class WarpField {
  readonly w: number;
  readonly h: number;
  readonly data: Uint8Array;

  constructor(
    worldW: number,
    worldH: number,
    private hexR: number,
  ) {
    this.w = Math.ceil(worldW * WARP_RES) + 1;
    this.h = Math.ceil(worldH * WARP_RES) + 1;
    this.data = new Uint8Array(this.w * this.h * 2);
    for (let j = 0; j < this.h; j++) {
      for (let i = 0; i < this.w; i++) {
        // texel centres, matching GL sampling of (i + 0.5) / w
        const [wx, wy] = warpRaw((i + 0.5) / WARP_RES, (j + 0.5) / WARP_RES);
        const o = (j * this.w + i) * 2;
        this.data[o] = Math.round((wx + 0.5) * 255);
        this.data[o + 1] = Math.round((wy + 0.5) * 255);
      }
    }
  }

  /** Warp offset (mu) at map point (x, y), bilinear like the GPU sampler. */
  offset(x: number, y: number): [number, number] {
    const fx = Math.min(this.w - 1, Math.max(0, x * WARP_RES - 0.5));
    const fy = Math.min(this.h - 1, Math.max(0, y * WARP_RES - 0.5));
    const i0 = Math.floor(fx);
    const j0 = Math.floor(fy);
    const i1 = Math.min(this.w - 1, i0 + 1);
    const j1 = Math.min(this.h - 1, j0 + 1);
    const tx = fx - i0;
    const ty = fy - j0;
    const at = (i: number, j: number, c: number) => this.data[(j * this.w + i) * 2 + c] / 255 - 0.5;
    const lerp = (c: number) => (at(i0, j0, c) * (1 - tx) + at(i1, j0, c) * tx) * (1 - ty) + (at(i0, j1, c) * (1 - tx) + at(i1, j1, c) * tx) * ty;
    const k = this.hexR * WARP_AMP;
    return [lerp(0) * k, lerp(1) * k];
  }
}

/** CPU-side hex lookup shared by the territory shader (as textures) and input picking. */
export interface HexLookup {
  cells: Int32Array;
  warp: WarpField;
}

export function buildHexLookup(world: LoadedWorld): HexLookup {
  return { cells: buildRenderCells(world), warp: new WarpField(world.width, world.height, world.extras.lattice.hexR) };
}

/**
 * Lattice cell → land hex for rendering: land cells map to themselves; water cells touching
 * land map to an adjacent land hex (so coastline slivers take the owner's colour); else −1.
 */
export function buildRenderCells(world: LoadedWorld): Int32Array {
  const { lattice, cellIndex } = world.extras;
  const { rows, cols } = lattice;
  const out = new Int32Array(rows * cols).fill(-1);
  for (let c = 0; c < out.length; c++) if (cellIndex[c] >= 0) out[c] = cellIndex[c];
  const DIRS = [
    [1, 0],
    [1, -1],
    [0, -1],
    [-1, 0],
    [-1, 1],
    [0, 1],
  ];
  for (let row = 0; row < rows; row++) {
    for (let col = 0; col < cols; col++) {
      const c = row * cols + col;
      if (cellIndex[c] >= 0) continue;
      const q = col - Math.floor(row / 2);
      for (const [dq, dr] of DIRS) {
        const r2 = row + dr;
        const c2 = q + dq + Math.floor(r2 / 2);
        if (r2 < 0 || r2 >= rows || c2 < 0 || c2 >= cols) continue;
        const v = cellIndex[r2 * cols + c2];
        if (v >= 0) {
          out[c] = v;
          break;
        }
      }
    }
  }
  return out;
}

/**
 * CPU twin of the shader lookup: land hex rendered at map point (x, y), or −1. With `cls`
 * (owner class per hex as the shader sees it: owner if known, −1 if never seen) it also
 * applies the rounded corners between owners.
 */
/** A hex's corner-rounding class (the territory shader's `roundClass`): −1 never seen, else the owner, foreign land +16 while live. */
export function roundClass(state: Uint8Array, h: number, me: number): number {
  const f = state[h * 4 + 3];
  if (!(f & HEX_SEEN)) return -1;
  const o = state[h * 4];
  return o === me || !(f & HEX_LIVE) ? o : o + 16;
}

export function renderHexAt(world: LoadedWorld, cells: Int32Array, warp: WarpField, x: number, y: number, cls?: (hex: number) => number): number {
  const { lattice } = world.extras;
  const [ox, oy] = warp.offset(x, y);
  const wx = x + ox;
  const wy = y + oy;
  const rf = (wy - lattice.originY) / (1.5 * lattice.hexR);
  const qf = (wx - lattice.originX) / (SQRT3 * lattice.hexR) - rf / 2;
  const sf = -qf - rf;
  let q = Math.round(qf);
  let r = Math.round(rf);
  const s = Math.round(sf);
  const dq = Math.abs(q - qf);
  const dr = Math.abs(r - rf);
  const ds = Math.abs(s - sf);
  if (dq > dr && dq > ds) q = -r - s;
  else if (dr > ds) r = -q - s;
  const at = (aq: number, ar: number) => {
    const c = aq + Math.floor(ar / 2);
    return ar < 0 || ar >= lattice.rows || c < 0 || c >= lattice.cols ? -1 : cells[ar * lattice.cols + c];
  };
  const h = at(q, r);
  if (!cls || h < 0) return h;
  // (a negative class is a class too: the edge of what we have seen rounds like a border)
  const c0 = cls(h);
  const R = lattice.hexR;
  const rx = wx - (lattice.originX + SQRT3 * R * (q + r * 0.5));
  const ry = wy - (lattice.originY + 1.5 * R * r);
  const apothem = 0.8660254 * R;
  const K = BORDER_ROUND * R;
  let sm = 1e9;
  let dmin = 1e9;
  let best = -1;
  for (let k = 0; k < 6; k++) {
    const d = apothem - (rx * EDGE_N[k][0] + ry * EDGE_N[k][1]);
    if (d > R) continue;
    const hk = at(q + EDGE_D[k][0], r + EDGE_D[k][1]);
    if (hk < 0 || hk === h) continue;
    const ck = cls(hk);
    if (ck === c0) continue;
    sm = smin(sm, d, K);
    if (d < dmin) {
      dmin = d;
      best = hk;
    }
  }
  return sm < 0 && best >= 0 ? best : h;
}

/** GLSL twin of the lookup above. */
export const GLSL_HEX = /* glsl */ `
uniform highp isampler2D uCells;   // lattice cell -> land hex (-1 none)
uniform highp usampler2D uState;   // per hex: owner, dens, rad, flags
uniform highp sampler2D uFlip;     // per hex: client time (s) of last owner change
uniform vec4 uLat;                 // originX, originY, hexR, warpAmp
uniform sampler2D uWarp;           // baked domain warp (WarpField)
uniform vec2 uWarpSize;            // warp field extent, mu
uniform ivec2 uCellDim;            // cols, rows
const float SQRT3 = 1.7320508;
const int STATE_W = ${STATE_W};

vec2 warpOffset(vec2 w) {
  return (texture(uWarp, w / uWarpSize).rg - 0.5) * uLat.z * uLat.w;
}
ivec2 axialOf(vec2 p) {
  float rf = (p.y - uLat.y) / (1.5 * uLat.z);
  float qf = (p.x - uLat.x) / (SQRT3 * uLat.z) - rf * 0.5;
  float sf = -qf - rf;
  float q = floor(qf + 0.5), r = floor(rf + 0.5), s = floor(sf + 0.5);
  float dq = abs(q - qf), dr = abs(r - rf), ds = abs(s - sf);
  if (dq > dr && dq > ds) q = -r - s; else if (dr > ds) r = -q - s;
  return ivec2(int(q), int(r));
}
vec2 axialCenter(ivec2 a) {
  return vec2(uLat.x + SQRT3 * uLat.z * (float(a.x) + float(a.y) * 0.5), uLat.y + 1.5 * uLat.z * float(a.y));
}
int hexAtAxial(ivec2 a) {
  int col = a.x + (a.y >> 1);
  if (a.y < 0 || a.y >= uCellDim.y || col < 0 || col >= uCellDim.x) return -1;
  return texelFetch(uCells, ivec2(col, a.y), 0).r;
}
uvec4 hexState(int h) { return texelFetch(uState, ivec2(h % STATE_W, h / STATE_W), 0); }
float hexFlip(int h) { return texelFetch(uFlip, ivec2(h % STATE_W, h / STATE_W), 0).r; }
`;
